import type { ComponentType, SVGProps } from 'react';
import React from 'react';

import { tv } from '../../tv';
import { Text } from '../text';

/**
 * One line of a dense list: a small glyph, a label that grows and truncates,
 * a hint after it, a figure at the trailing edge. 32px tall, so a list of
 * them has a rhythm and a row that changes words does not move its
 * neighbours. It reads one type step below the body in the muted role;
 * emphasis and weight lift a row that matters (the one running, the one that
 * failed). The row owns its glyph: the caller passes the component and the
 * row sizes and colours it (icon.md, "Two patterns").
 *
 * `ListRow.Group` nests rows under a parent row: indented one step, with a
 * hairline down the glyph column so the eye reads them as one branch.
 */

const rowStyles = tv({
  slots: {
    root: 'ck:flex ck:w-full ck:min-w-0 ck:flex-1 ck:items-center ck:gap-2',
    glyph: 'ck:size-3 ck:shrink-0',
    body: 'ck:flex ck:min-w-0 ck:flex-1 ck:items-baseline ck:gap-1 ck:overflow-hidden',
    trail: 'ck:flex ck:shrink-0 ck:items-center ck:gap-2 ck:tabular-nums',
  },
  variants: {
    tone: {
      muted: { glyph: 'ck:text-text-muted' },
      default: { glyph: 'ck:text-text-default' },
      error: { glyph: 'ck:text-error-text' },
    },
    /* md is a line that stands alone; sm is a step inside a list, where the
     * rows read as one block and the taller row would read as air. */
    size: {
      md: { root: 'ck:min-h-8' },
      sm: { root: 'ck:min-h-6' },
    },
    isDimmed: { true: { root: 'ck:opacity-50' }, false: {} },
    isStruck: { true: { root: 'ck:line-through' }, false: {} },
  },
  defaultVariants: { tone: 'muted', size: 'md', isDimmed: false, isStruck: false },
});

const groupStyles = tv({
  /* The branch line is the group's own left border at the parent's edge; the
   * rows start one step in, so a group's text aligns with a group's text and
   * nothing floats between the parent's edge and its rows. Inside a
   * disclosure the trigger and the panel share one edge, so the rail sits at
   * the parent row's text edge, not at its glyph's. */
  base: 'ck:flex ck:flex-col ck:border-l ck:border-border-divider ck:pl-3',
});

export type ListRowGlyph = ComponentType<SVGProps<SVGSVGElement>>;

export interface ListRowProps {
  /** The row's glyph, sized and coloured by the row. Absent: the label starts at the edge. */
  glyph?: ListRowGlyph;
  /** The glyph's colour role; error is the one saturated role a list may show. */
  tone?: 'muted' | 'default' | 'error';
  label: React.ReactNode;
  /** Words after the label, dimmer: what tells two similar rows apart. */
  hint?: string | null;
  /** The trailing figure: a duration, a counter, a count. Tabular numerals. */
  trail?: React.ReactNode;
  /** The label's colour: muted is the list's voice; default lifts one row; error names the one that failed. */
  emphasis?: 'muted' | 'default' | 'error';
  /** 32px for a line on its own, 24px for a step inside a list. */
  size?: 'md' | 'sm';
  weight?: 'regular' | 'medium';
  /** Work is happening behind the label: it shimmers (Text isLive). */
  isLive?: boolean;
  /** A step a stop interrupted. */
  isStruck?: boolean;
  /** A step not started yet. */
  isDimmed?: boolean;
  id?: string;
}

const Root = React.forwardRef<HTMLDivElement, ListRowProps>(function ListRow(
  {
    glyph: Glyph,
    tone = 'muted',
    label,
    hint,
    trail,
    emphasis = 'muted',
    size = 'md',
    weight = 'regular',
    isLive = false,
    isStruck = false,
    isDimmed = false,
    ...props
  },
  ref,
) {
  const { root, glyph, body, trail: trailSlot } = rowStyles({ tone, size, isDimmed, isStruck });
  return (
    <div {...props} ref={ref} className={root()} data-component="list-row">
      {Glyph ? <Glyph className={glyph()} aria-hidden="true" /> : null}
      <div className={body()}>
        <Text as="span" size="xs" weight={weight} color={emphasis} isLive={isLive} isTruncated>
          {label}
        </Text>
        {hint ? (
          <Text as="span" size="xs" color="muted" isTruncated>
            · {hint}
          </Text>
        ) : null}
      </div>
      {trail ? <div className={trailSlot()}>{trail}</div> : null}
    </div>
  );
});

export interface ListRowGroupProps {
  children: React.ReactNode;
  id?: string;
}

const Group = React.forwardRef<HTMLDivElement, ListRowGroupProps>(function ListRowGroup(
  { children, ...props },
  ref,
) {
  return (
    <div {...props} ref={ref} className={groupStyles()} data-component="list-row-group">
      {children}
    </div>
  );
});

export const ListRow = Object.assign(Root, { Group });
